// API Utilities with rate limiting and caching

import { toStateAbbr } from '../data/states';

class APIRateLimiter {
  constructor(maxRequests, timeWindow) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow; // in milliseconds
    this.requests = [];
  }

  async throttle() {
    const now = Date.now();

    // Remove requests outside the time window
    this.requests = this.requests.filter(
      timestamp => now - timestamp < this.timeWindow
    );

    if (this.requests.length >= this.maxRequests) {
      // Calculate wait time
      const oldestRequest = this.requests[0];
      const waitTime = this.timeWindow - (now - oldestRequest);

      console.warn(`Rate limit reached. Waiting ${waitTime}ms...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      return this.throttle(); // Retry after waiting
    }

    this.requests.push(now);
  }
}

class APICache {
  constructor(ttl = 300000) { // Default 5 minutes TTL
    this.cache = new Map();
    this.ttl = ttl;
  }

  set(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  get(key) {
    const item = this.cache.get(key);

    if (!item) return null;

    const age = Date.now() - item.timestamp;
    if (age > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  clear() {
    this.cache.clear();
  }

  size() {
    return this.cache.size;
  }
}

// Rate limiters for different APIs
const censusRateLimiter = new APIRateLimiter(50, 60000); // 50 requests per minute
const nominatimRateLimiter = new APIRateLimiter(1, 1000); // 1 request per second per Nominatim policy

// Caches
const geocodeCache = new APICache(3600000); // 1 hour
const censusCache = new APICache(86400000); // 24 hours

// Census redirects keyless requests to an HTML "missing key" page whose
// response has no CORS headers, so fetch() rejects with a generic TypeError
// before we can read a status. Catch that case at the URL level and fail
// fast with a useful message instead of three pointless retries.
function diagnoseFetchError(url, error) {
  const isCensus = /(^|\.)api\.census\.gov$/.test(new URL(url, 'https://x').hostname);
  const hasKey = /[?&]key=/.test(url);
  if (isCensus && !hasKey) {
    return new Error(
      'Census API key missing or invalid. Set REACT_APP_CENSUS_API_KEY in the build environment.'
    );
  }
  return error;
}

// Enhanced fetch with retry logic
export async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  let lastError;

  // Skip retries for unrecoverable configuration errors
  const isCensus = /(^|\.)api\.census\.gov$/.test(new URL(url, 'https://x').hostname);
  if (isCensus && !/[?&]key=/.test(url)) {
    throw diagnoseFetchError(url, null);
  }

  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      lastError = error;
      console.warn(`Fetch attempt ${i + 1} failed:`, error.message);

      if (i < maxRetries - 1) {
        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, i), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw diagnoseFetchError(url, new Error(`Failed after ${maxRetries} attempts: ${lastError.message}`));
}

// How many raw results to ask Nominatim for. One request either way, so the
// extra rows are free — and without them there's no way to tell a unique place
// from one of the 31 Washingtons.
const GEOCODE_CANDIDATE_LIMIT = 10;

/** USPS abbreviation for a Nominatim address block, when it names a US state. */
function addressState(address) {
  const full = address?.state;
  if (!full) return null;
  return toStateAbbr(full);
}

/** The most place-like label Nominatim gives us for an address block. */
function addressPlace(address, displayName) {
  return address?.city || address?.town || address?.village || address?.hamlet ||
         address?.county || displayName.split(',')[0].trim();
}

/**
 * Raw Nominatim rows for a query, cached.
 *
 * geocodeCandidates and geocodeWithCache both need these, and a search runs
 * both. Sharing one cache key means that costs one request, not two — which
 * matters when the budget is one request per second.
 */
async function fetchGeocodeRows(location) {
  const cacheKey = `geo-rows:${location.toLowerCase()}`;
  const cached = geocodeCache.get(cacheKey);
  if (cached) return cached;

  await nominatimRateLimiter.throttle();

  const response = await fetchWithRetry(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&countrycodes=us&limit=${GEOCODE_CANDIDATE_LIMIT}&addressdetails=1`
  );

  const data = await response.json();
  if (!data || data.length === 0) {
    throw new Error('Location not found');
  }

  geocodeCache.set(cacheKey, data);
  return data;
}

/** Case- and punctuation-insensitive form, for comparing two place names. */
function normalizePlace(name) {
  return String(name).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Geocode a query and return every distinct place it could mean.
 *
 * Nominatim happily returns eight rows for one town — a node, a boundary, a
 * post office — so results are collapsed to one entry per (place, state) pair.
 * What's left is genuine ambiguity: the Springfields, the Washingtons.
 */
export async function geocodeCandidates(location) {
  const data = await fetchGeocodeRows(location);

  const seen = new Set();
  const candidates = [];

  for (const row of data) {
    const address = row.address || {};
    const displayName = row.display_name || '';
    const state = addressState(address);
    const place = addressPlace(address, displayName);
    // Same town, different OSM feature — keep the first, which Nominatim
    // ranked highest.
    const key = `${normalizePlace(place)}|${state || '??'}`;
    if (seen.has(key)) continue;
    seen.add(key);

    candidates.push({
      lat: parseFloat(row.lat),
      lng: parseFloat(row.lon),
      displayName,
      address,
      place,
      state,
      county: address.county || null,
      label: state ? `${place}, ${state}` : place,
      // Re-querying by this string resolves the same place unambiguously,
      // which lets a picked candidate re-enter the normal search path.
      query: state ? `${place}, ${state}` : displayName,
    });
  }

  return candidates;
}

/**
 * The candidates that share the top result's name in a *different* state.
 *
 * This is deliberately narrow. "Springfield" returning a city and its
 * surrounding township is not ambiguity worth interrupting for; "Springfield"
 * in Missouri and Illinois is. Returns [] when there's nothing to ask about.
 */
export function nameCollisions(candidates) {
  if (!candidates || candidates.length < 2) return [];
  const topName = normalizePlace(candidates[0].place);
  const sameName = candidates.filter(c => normalizePlace(c.place) === topName);
  const states = new Set(sameName.map(c => c.state).filter(Boolean));
  return states.size > 1 ? sameName : [];
}

// Geocoding with rate limiting and caching
export async function geocodeWithCache(location) {
  const cacheKey = `geocode:${location.toLowerCase()}`;
  const cached = geocodeCache.get(cacheKey);

  if (cached) {
    // eslint-disable-next-line no-console
    console.log('Geocode cache hit for:', location);
    return cached;
  }

  const data = await fetchGeocodeRows(location);

  // data[0] is the same row a limit=1 request would have returned, so asking
  // for more rows changes nothing about which place gets scored — it only lets
  // us report how many others were in contention.
  const distinctPlaces = new Set(
    data.map(r => `${normalizePlace(addressPlace(r.address || {}, r.display_name || ''))}|${addressState(r.address) || '??'}`)
  );

  const lat = parseFloat(data[0].lat);
  const lng = parseFloat(data[0].lon);

  // City/place searches often lack a postcode — fall back to reverse geocode
  // at the same coordinates, which nearly always returns one.
  let postcode = data[0].address?.postcode
    ? String(data[0].address.postcode).trim().split('-')[0].substring(0, 5)
    : null;

  if (!postcode) {
    try {
      await nominatimRateLimiter.throttle();
      const revResp = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      if (revResp.ok) {
        const revData = await revResp.json();
        if (revData.address?.postcode) {
          postcode = String(revData.address.postcode).trim().split('-')[0].substring(0, 5);
        }
      }
    } catch {
      // reverse geocode failed — postcode stays null, RUCA will be skipped
    }
  }

  const result = {
    lat,
    lng,
    displayName: data[0].display_name,
    address: data[0].address,
    postcode,
    // Distinct places the query could also have meant. 1 means the query was
    // unambiguous; anything higher means this result was a pick among several.
    matchCount: distinctPlaces.size,
  };

  geocodeCache.set(cacheKey, result);
  return result;
}

// getLandArea is no longer needed as a standalone function —
// getCountyFromCoordinates returns areaSqMiles from TIGERweb.
// This stub keeps any legacy imports from breaking.
export async function getLandArea(_stateFips, _countyFips) {
  return { areaSqMiles: AREA_FALLBACK_SQ_MI, areaSqMeters: AREA_FALLBACK_SQ_MI * SQ_M_PER_SQ_MI };
}

// Census API with rate limiting and caching
export async function fetchCensusData(stateFips, countyFips) {
  const cacheKey = `census:${stateFips}:${countyFips}`;
  const cached = censusCache.get(cacheKey);

  if (cached) {
    // eslint-disable-next-line no-console
    console.log('Census cache hit for:', cacheKey);
    return cached;
  }

  await censusRateLimiter.throttle();

  const apiKey = process.env.REACT_APP_CENSUS_API_KEY;
  const apiKeyParam = apiKey ? `&key=${apiKey}` : '';

  // Variables: Population, Median Income, Housing Units, Commute Time, Labor Force, Unemployed, Median Age
  const url = `https://api.census.gov/data/2022/acs/acs5?get=B01003_001E,B19013_001E,B25001_001E,B08303_001E,B23025_002E,B23025_005E,B01002_001E&for=county:${countyFips}&in=state:${stateFips}${apiKeyParam}`;

  const response = await fetchWithRetry(url);
  const data = await response.json();

  if (!data || data.length < 2) {
    throw new Error('No Census data found');
  }

  const values = data[1];
  const laborForce = parseInt(values[4]) || 0;
  const unemployed = parseInt(values[5]) || 0;
  const result = {
    totalPopulation: parseInt(values[0]) || 0,
    medianIncome: parseInt(values[1]) || 0,
    housingUnits: parseInt(values[2]) || 0,
    commuteTime: parseFloat(values[3]) || 0,
    laborForce,
    unemployed,
    medianAge: parseFloat(values[6]) || 0,
    // Use parsed numbers — the raw strings can be null for disclosure-
    // suppressed tracts, which would otherwise yield NaN% in the UI.
    unemploymentRate: laborForce > 0
      ? parseFloat(((unemployed / laborForce) * 100).toFixed(1))
      : 0
  };

  censusCache.set(cacheKey, result);
  return result;
}

// ── Coordinate → county ──────────────────────────────────────────────────────
// Returns { stateFips (2-digit), countyFips (3-digit), countyName, areaSqMiles }.
//
// Primary: Census TIGERweb, current-vintage Counties layer. One point-in-polygon
// query returns the county (or county equivalent) FIPS, name and land area.
// "Current" matters for Connecticut: its county equivalents have been the nine
// planning regions (09110–09190) in every Census product since 2022, and the
// ACS no longer answers for the legacy counties (09001–09015).
//
// Fallback: the FCC Census Area API, which is 2020-vintage geography and still
// returns the legacy Connecticut counties. It is only used when TIGERweb fails,
// and it carries no land area, so density then uses a coarse fallback.
const TIGER_COUNTIES_URL =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_Current/MapServer/82/query';
const SQ_M_PER_SQ_MI = 2589988.11;
export const AREA_FALLBACK_SQ_MI = 1000;
const LOOKUP_TIMEOUT_MS = 8000;

// A hung request would otherwise never reach the fallback. AbortSignal.timeout
// is missing from some test and older browser runtimes, so degrade to no signal.
const timeoutSignal = () =>
  (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function')
    ? AbortSignal.timeout(LOOKUP_TIMEOUT_MS)
    : undefined;

function normalizeFips(state, county) {
  // FCC county_fips is usually 5 chars (state+county) but can come back
  // without leading zeros (e.g. "2013" for 02013 in AK); pad and split.
  const stateFips = String(state || '').padStart(2, '0');
  const countyFips = String(county || '').padStart(5, '0').slice(-3);
  return { stateFips, countyFips };
}

async function lookupCountyTiger(lat, lng) {
  const params = new URLSearchParams({
    geometry: `${lng},${lat}`,
    geometryType: 'esriGeometryPoint',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'GEOID,STATE,COUNTY,NAME,AREALAND',
    returnGeometry: 'false',
    f: 'json',
  });
  const resp = await fetch(`${TIGER_COUNTIES_URL}?${params}`, { signal: timeoutSignal() });
  if (!resp.ok) throw new Error(`TIGERweb error: ${resp.status}`);
  const data = await resp.json();
  if (data.error) throw new Error(`TIGERweb error: ${data.error.message || data.error.code}`);
  const attrs = data.features?.[0]?.attributes;
  if (!attrs || !attrs.STATE || !attrs.COUNTY) {
    throw new Error('TIGERweb returned no county for this location');
  }
  const areaLand = Number(attrs.AREALAND);
  return {
    ...normalizeFips(attrs.STATE, attrs.COUNTY),
    countyName: attrs.NAME || '',
    areaSqMiles: areaLand > 0 ? parseFloat((areaLand / SQ_M_PER_SQ_MI).toFixed(2)) : AREA_FALLBACK_SQ_MI,
  };
}

async function lookupCountyFcc(lat, lng) {
  const fccUrl = `https://geo.fcc.gov/api/census/area?lat=${lat}&lon=${lng}&format=json`;
  const resp = await fetch(fccUrl, { signal: timeoutSignal() });
  if (!resp.ok) throw new Error(`FCC API error: ${resp.status}`);
  const data = await resp.json();
  if (!data.results || data.results.length === 0) {
    throw new Error('Could not identify county for this location');
  }
  const r = data.results[0];
  if (!r.state_fips || !r.county_fips) throw new Error('FCC API returned no county FIPS for this location');
  return {
    ...normalizeFips(r.state_fips, r.county_fips),
    countyName: r.county_name || '',
    areaSqMiles: AREA_FALLBACK_SQ_MI,
  };
}

export async function getCountyFromCoordinates(lat, lng) {
  const cacheKey = `county:${lat.toFixed(4)}:${lng.toFixed(4)}`;
  const cached = geocodeCache.get(cacheKey);
  if (cached) return cached;

  try {
    const result = await lookupCountyTiger(lat, lng);
    geocodeCache.set(cacheKey, result);
    return result;
  } catch (tigerErr) {
    // eslint-disable-next-line no-console
    console.warn('TIGERweb county lookup failed, falling back to FCC:', tigerErr.message);
    try {
      // Not cached: a fallback result carries a placeholder land area, and a
      // brief TIGERweb blip should not pin a coarse density for an hour.
      return await lookupCountyFcc(lat, lng);
    } catch (fccErr) {
      throw new Error(`County lookup failed (TIGERweb: ${tigerErr.message}; FCC: ${fccErr.message})`);
    }
  }
}

// Alias for any legacy callers
export const getFipsFromCoordinates = getCountyFromCoordinates;

// Multi-year ACS data for trends (2018–2022 5-year estimates)
// Fetches population, income, and unemployment for each year separately.
// Runs in parallel; individual year failures return null and are filtered out.
export async function fetchMultiYearCensusData(stateFips, countyFips) {
  const cacheKey = `multiyear:${stateFips}:${countyFips}`;
  const cached = censusCache.get(cacheKey);
  if (cached) return cached;

  const apiKey = process.env.REACT_APP_CENSUS_API_KEY;
  const apiKeyParam = apiKey ? `&key=${apiKey}` : '';
  const years = [2018, 2019, 2020, 2021, 2022];

  const results = await Promise.all(
    years.map(async (year) => {
      await censusRateLimiter.throttle();
      const url =
        `https://api.census.gov/data/${year}/acs/acs5` +
        `?get=B01003_001E,B19013_001E,B23025_002E,B23025_005E` +
        `&for=county:${countyFips}&in=state:${stateFips}${apiKeyParam}`;
      try {
        const response = await fetchWithRetry(url);
        const data = await response.json();
        if (!data || data.length < 2) return null;
        const v = data[1];
        const laborForce = parseInt(v[2]) || 0;
        const unemployed  = parseInt(v[3]) || 0;
        return {
          year,
          population:      parseInt(v[0]) || 0,
          medianIncome:    parseInt(v[1]) || 0,
          unemploymentRate: laborForce > 0
            ? parseFloat(((unemployed / laborForce) * 100).toFixed(1))
            : 0
        };
      } catch {
        return null;
      }
    })
  );

  const filtered = results.filter(Boolean);
  censusCache.set(cacheKey, filtered);
  return filtered;
}

// Clear all caches (useful for admin or settings)
export function clearAllCaches() {
  geocodeCache.clear();
  censusCache.clear();
  // eslint-disable-next-line no-console
  console.log('All caches cleared');
}

// Get cache stats (useful for debugging)
export function getCacheStats() {
  return {
    geocodeCache: geocodeCache.size(),
    censusCache: censusCache.size()
  };
}

const apiUtils = {
  fetchWithRetry,
  geocodeWithCache,
  geocodeCandidates,
  nameCollisions,
  fetchCensusData,
  getCountyFromCoordinates,
  getFipsFromCoordinates,
  clearAllCaches,
  getCacheStats
};
export default apiUtils;
